import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, Star, FileText, Eye, CheckCircle2, X, LogOut, Mail, Search, Download } from "lucide-react";
import { toast } from "sonner";
import { fetchAllArticles, createArticle, updateArticle, deleteArticle, CATEGORIES, catLabel, timeAgo, adminMe, adminLogout, fetchSubscribers, deleteSubscriber } from "@/lib/api";
import AdminLogin from "@/components/AdminLogin";
import AdminAnalytics from "@/components/AdminAnalytics";
import AdminSettings from "@/components/AdminSettings";
import AdminInstagram from "@/components/AdminInstagram";
import AdminYoutube from "@/components/AdminYoutube";

const EMPTY = { title: "", summary: "", content: "", category: "india", image_url: "", author: "खबरिक डेस्क", featured: false, published: true };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono-meta text-[10px] text-slate-400 uppercase tracking-widest block mb-1.5">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full bg-[#0D0D10] border border-white/15 text-white text-sm px-3 py-2.5 focus:border-[#FFD400] focus:outline-none transition-colors placeholder:text-slate-600";

export default function AdminPage() {
  const [auth, setAuth] = useState("checking");
  const [articles, setArticles] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [tab, setTab] = useState("articles");
  const [filterQ, setFilterQ] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [confirmId, setConfirmId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    fetchAllArticles().then(setArticles);
    fetchSubscribers().then(setSubscribers).catch(() => {});
  };
  useEffect(() => {
    adminMe()
      .then((u) => {
        setAuth(u);
        load();
      })
      .catch(() => setAuth(false));
  }, []);

  const logout = async () => {
    await adminLogout();
    setAuth(false);
    toast.success("Logged out");
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.summary.trim() || !form.content.trim()) {
      toast.error("Title, summary and content are required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateArticle(editingId, form);
        toast.success("Article updated!");
      } else {
        await createArticle(form);
        toast.success("Article published!");
      }
      setForm(EMPTY);
      setEditingId(null);
      setShowForm(false);
      load();
    } catch {
      toast.error("Something went wrong, please try again");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (a) => {
    setForm({ title: a.title, summary: a.summary, content: a.content, category: a.category, image_url: a.image_url, author: a.author, featured: a.featured, published: a.published });
    setEditingId(a.id);
    setShowForm(true);
    window.scrollTo({ top: 0 });
  };

  const remove = async (id) => {
    if (confirmId !== id) {
      setConfirmId(id);
      setTimeout(() => setConfirmId((c) => (c === id ? null : c)), 3000);
      return;
    }
    setConfirmId(null);
    await deleteArticle(id);
    toast.success("Article deleted");
    load();
  };

  const removeSub = async (id) => {
    await deleteSubscriber(id);
    toast.success("Subscriber removed");
    load();
  };

  const exportCsv = () => {
    const rows = [["email", "subscribed_at"], ...subscribers.map((s) => [s.email, s.created_at])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "khabrik-subscribers.csv";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("CSV downloaded");
  };

  const filteredArticles = articles.filter(
    (a) =>
      (filterCat === "all" || a.category === filterCat) &&
      (!filterQ.trim() || a.title.toLowerCase().includes(filterQ.toLowerCase()) || a.author.toLowerCase().includes(filterQ.toLowerCase()))
  );

  const stats = {
    total: articles.length,
    published: articles.filter((a) => a.published).length,
    views: articles.reduce((s, a) => s + a.views, 0),
  };

  if (auth === "checking") {
    return (
      <main className="pt-48 pb-40 text-center font-mono-meta text-xs text-slate-500 uppercase tracking-widest">
        Checking session...
      </main>
    );
  }
  if (auth === false) {
    return (
      <AdminLogin
        onSuccess={(u) => {
          setAuth(u);
          load();
        }}
      />
    );
  }

  return (
    <main className="pt-32 pb-16 max-w-7xl mx-auto px-4 sm:px-6" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <span className="font-mono-meta text-xs text-[#FFD400] uppercase tracking-[0.3em]">Editor Dashboard</span>
          <h1 className="font-display text-4xl text-white mt-2">Admin Panel</h1>
          <p className="font-mono-meta text-[10px] text-slate-500 mt-2">{auth.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex items-center gap-2 border border-white/15 text-slate-300 px-4 py-2.5 text-sm font-semibold hover:text-[#FF453A] hover:border-[#FF453A] transition-colors"
          >
            <LogOut size={15} /> Logout
          </button>
          <button
            data-testid="new-article-button"
            onClick={() => {
              setForm(EMPTY);
              setEditingId(null);
              setShowForm(!showForm);
            }}
            className="flex items-center gap-2 bg-[#FFD400] text-[#0D0D10] px-5 py-2.5 text-sm font-bold hover:bg-[#E6BE00] transition-colors"
          >
            {showForm ? <X size={16} /> : <Plus size={16} />}
            {showForm ? "Close" : "New Article"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
        {[
          { icon: FileText, label: "Total Articles", value: stats.total },
          { icon: CheckCircle2, label: "Published", value: stats.published },
          { icon: Eye, label: "Total Views", value: stats.views.toLocaleString("en-IN") },
          { icon: Mail, label: "Subscribers", value: subscribers.length },
        ].map((s) => (
          <div key={s.label} className="bg-[#0D0D10] p-5 sm:p-6">
            <s.icon size={16} className="text-[#FFD400]" />
            <p className="font-display text-2xl sm:text-3xl text-white mt-3">{s.value}</p>
            <p className="font-mono-meta text-[10px] text-slate-500 uppercase tracking-widest mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-1 mb-8 border-b border-white/10">
        {[
          { id: "articles", label: "Articles" },
          { id: "subscribers", label: "Subscribers" },
          { id: "instagram", label: "Instagram" },
          { id: "youtube", label: "YouTube" },
          { id: "analytics", label: "Analytics" },
          { id: "settings", label: "Settings" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
              tab === t.id ? "text-[#FFD400] border-[#FFD400]" : "text-slate-400 border-transparent hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "articles" && showForm && (
        <motion.form
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={submit}
          className="bg-[#15151A] border border-white/10 p-6 sm:p-8 mb-10 space-y-5"
          data-testid="admin-article-form"
        >
          <h2 className="font-editorial font-bold text-white text-xl">{editingId ? "Edit Article" : "Write New Article"}</h2>
          <Field label="Title *">
            <input data-testid="admin-article-title-input" className={inputCls} value={form.title} onChange={set("title")} placeholder="Article headline (Hindi)..." />
          </Field>
          <Field label="Summary *">
            <textarea data-testid="admin-article-summary-input" className={inputCls} rows={2} value={form.summary} onChange={set("summary")} placeholder="Short summary..." />
          </Field>
          <Field label="Content * (separate paragraphs with a blank line)">
            <textarea data-testid="admin-article-content-input" className={inputCls} rows={8} value={form.content} onChange={set("content")} placeholder="Write the full story here..." />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Category">
              <select data-testid="admin-article-category-select" className={inputCls} value={form.category} onChange={set("category")}>
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.hi}</option>
                ))}
              </select>
            </Field>
            <Field label="Author">
              <input data-testid="admin-article-author-input" className={inputCls} value={form.author} onChange={set("author")} />
            </Field>
            <Field label="Image URL">
              <input data-testid="admin-article-image-input" className={inputCls} value={form.image_url} onChange={set("image_url")} placeholder="https://..." />
            </Field>
          </div>
          {form.image_url && (
            <img src={form.image_url} alt="preview" className="h-32 object-cover border border-white/10" onError={(e) => (e.target.style.display = "none")} />
          )}
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" data-testid="admin-article-featured-checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#FFD400] w-4 h-4" />
              <Star size={14} className="text-[#FFD400]" /> Mark as Featured
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-[#FFD400] w-4 h-4" />
              Publish
            </label>
            <button
              type="submit"
              disabled={saving}
              data-testid="admin-publish-article-button"
              className="ml-auto bg-[#FFD400] text-[#0D0D10] px-8 py-2.5 text-sm font-bold hover:bg-[#E6BE00] transition-colors disabled:opacity-50"
            >
              {saving ? "Saving..." : editingId ? "Update" : "Publish"}
            </button>
          </div>
        </motion.form>
      )}

      {tab === "articles" && (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                data-testid="admin-filter-search-input"
                value={filterQ}
                onChange={(e) => setFilterQ(e.target.value)}
                placeholder="Search by title or author..."
                className="w-full bg-[#15151A] border border-white/15 text-white text-sm pl-9 pr-3 py-2.5 focus:border-[#FFD400] focus:outline-none transition-colors placeholder:text-slate-600"
              />
            </div>
            <select
              data-testid="admin-filter-category-select"
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
              className="bg-[#15151A] border border-white/15 text-white text-sm px-3 py-2.5 focus:border-[#FFD400] focus:outline-none"
            >
              <option value="all">All Categories</option>
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>{c.hi}</option>
              ))}
            </select>
          </div>
          <div className="border border-white/10 divide-y divide-white/[0.06]" data-testid="admin-articles-list">
            {filteredArticles.length === 0 && (
              <p className="p-6 text-sm text-slate-400">
                {articles.length === 0 ? 'No articles yet. Click "New Article" to publish your first story.' : "No articles match your filters."}
              </p>
            )}
            {filteredArticles.map((a) => (
          <div key={a.id} className="flex items-center gap-4 p-4 hover:bg-white/[0.02] transition-colors" data-testid={`admin-article-row-${a.id}`}>
            <img src={a.image_url} alt="" className="w-16 h-12 object-cover border border-white/10 hidden sm:block" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate flex items-center gap-2">
                {a.featured && <Star size={12} className="text-[#FFD400] flex-shrink-0" fill="#FFD400" />}
                {a.title}
              </p>
              <p className="font-mono-meta text-[10px] text-slate-500 mt-1">
                {catLabel(a.category)} · {a.author} · {timeAgo(a.created_at)} · {a.views.toLocaleString("en-IN")} views
                {!a.published && <span className="text-[#FF453A] ml-2">[DRAFT]</span>}
              </p>
            </div>
            <button onClick={() => startEdit(a)} data-testid={`admin-edit-${a.id}`} className="p-2 text-slate-400 hover:text-[#FFD400] transition-colors">
              <Pencil size={15} />
            </button>
            <button
              onClick={() => remove(a.id)}
              data-testid={`admin-delete-${a.id}`}
              className={`p-2 transition-colors flex items-center gap-1 ${confirmId === a.id ? "text-[#FF453A]" : "text-slate-400 hover:text-[#FF453A]"}`}
            >
              <Trash2 size={15} />
              {confirmId === a.id && <span className="text-[10px] font-bold">Sure?</span>}
            </button>
          </div>
        ))}
          </div>
        </>
      )}

      {tab === "subscribers" && (
        <>
          <div className="flex justify-end mb-4">
            <button
              data-testid="export-subscribers-csv-button"
              onClick={exportCsv}
              disabled={subscribers.length === 0}
              className="flex items-center gap-2 border border-white/15 text-slate-300 px-4 py-2 text-sm font-semibold hover:text-[#FFD400] hover:border-[#FFD400] transition-colors disabled:opacity-50"
            >
              <Download size={14} /> Download CSV
            </button>
          </div>
          <div className="border border-white/10 divide-y divide-white/[0.06]" data-testid="admin-subscribers-list">
            {subscribers.length === 0 && (
              <p className="p-6 text-sm text-slate-400">No subscribers yet.</p>
            )}
            {subscribers.map((s, i) => (
              <div key={s.id} className="flex items-center gap-4 p-4 hover:bg-white/[0.02] transition-colors" data-testid={`admin-subscriber-row-${s.id}`}>
                <span className="font-mono-meta text-[10px] text-slate-500 w-8">{String(i + 1).padStart(2, "0")}</span>
                <Mail size={14} className="text-[#FFD400] flex-shrink-0" />
                <p className="flex-1 text-sm font-semibold text-white truncate">{s.email}</p>
                <span className="font-mono-meta text-[10px] text-slate-500">{timeAgo(s.created_at)}</span>
                <button
                  onClick={() => removeSub(s.id)}
                  data-testid={`admin-delete-subscriber-${s.id}`}
                  className="p-2 text-slate-400 hover:text-[#FF453A] transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "instagram" && <AdminInstagram onSynced={load} />}

      {tab === "youtube" && <AdminYoutube onSynced={load} />}

      {tab === "analytics" && <AdminAnalytics articles={articles} />}

      {tab === "settings" && <AdminSettings email={auth.email} />}
    </main>
  );
}
